import React, { useState, useEffect } from 'react';
import { Box, IconButton, Button, Typography, ToggleButton, ToggleButtonGroup, CircularProgress, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    RiArrowLeftLine,
    RiDownloadLine,
    RiPlayLine,
    RiImageLine,
    RiVideoLine,
    RiCloseLargeLine,
    RiExportFill,
    RiImageFill,
    RiSaveFill,
    RiPencilLine,
    RiCheckLine,
    RiEditLine,
} from '@remixicon/react';
import { setDesignMode, setIsPlaying, setCanvasId, setProjectName } from '../../redux/slices/canvasSlice';
import ExportDialog from '../ExportDialog';
import { colors, naturalScale } from '../../../theme/tokens';
import { updateCanvasConfig } from '../../../services/s3Service';

/**
 * Top Header Bar
 * 
 * Left Side: Exit | Export | Preview | Video/Image Toggle
 * Right Side: Logo | Project Name
 */
const TopHeaderBar = ({ projectName, storeId }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const canvasState = useSelector((state) => state.canvas);
    const { designMode, isPlaying } = canvasState;

    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [editedName, setEditedName] = useState(projectName);

    // Sync editedName when projectName changes from outside
    useEffect(() => {
        if (!isEditingName) {
            setEditedName(projectName);
        }
    }, [projectName, isEditingName]);

    const handleExit = () => {
        if (window.confirm('Are you sure you want to exit? Unsaved changes will be lost.')) {
            navigate(-1); // Go back to previous page
        }
    };

    const handleDesignModeChange = (event, newMode) => {
        if (newMode !== null) {
            dispatch(setDesignMode(newMode));
        }
    };

    const handlePreview = () => {
        dispatch(setIsPlaying(!isPlaying));
    };

    const handleSave = async () => {
        if (!storeId) {
            alert('Store ID is missing. Cannot save canvas.');
            return;
        }

        setSaving(true);
        try {
            // Generate ID if it doesn't exist
            let canvasId = canvasState.canvasId;
            if (!canvasId) {
                canvasId = `canvas-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                dispatch(setCanvasId(canvasId));
            }

            // Get the full canvas state from Redux
            const canvasData = {
                ...canvasState,
                id: canvasId, // Include ID in the saved data
                savedAt: new Date().toISOString(),
            };
            
            // Read existing config, replace if ID exists or add as new, and write back to S3
            await updateCanvasConfig(storeId, canvasData);
            console.log('canvasData', canvasData);
            alert('Canvas saved successfully!');
        } catch (error) {
            console.error('Error saving canvas:', error);
            alert(`Failed to save canvas: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleEditName = () => {
        setIsEditingName(true);
        setEditedName(projectName);
    };

    const handleSaveName = () => {
        if (editedName.trim()) {
            dispatch(setProjectName(editedName.trim()));
        }
        setIsEditingName(false);
    };

    const handleCancelEditName = () => {
        setEditedName(projectName);
        setIsEditingName(false);
    };

    const handleNameKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSaveName();
        } else if (e.key === 'Escape') {
            handleCancelEditName();
        }
    };

    return (
        <>
            <Box
                sx={{
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 24px',
                    backgroundColor: 'white',
                    borderBottom: '1px solid #E0E0E0',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                    zIndex: 1000,
                }}
            >
                {/* Left Side */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    {/* DigiSlides Logo */}
                    <Box
                        component="img"
                        src="/logo192.svg"
                        alt="DigiSlides"
                        sx={{
                            height: 40,
                            width: 40,
                        }}
                    />

                    {/* Project Name */}
                    {isEditingName ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                                value={editedName}
                                onChange={(e) => setEditedName(e.target.value)}
                                onKeyDown={handleNameKeyPress}
                                autoFocus
                                variant="standard"
                                sx={{
                                    '& .MuiInputBase-input': {
                                        fontSize: '1.25rem',
                                        fontWeight: 600,
                                        color: colors.neutral.gray800,
                                        padding: '4px 0',
                                    },
                                    '& .MuiInput-underline:before': {
                                        borderBottomColor: colors.neutral.gray300,
                                    },
                                    '& .MuiInput-underline:hover:before': {
                                        borderBottomColor: colors.neutral.gray400,
                                    },
                                    '& .MuiInput-underline:after': {
                                        borderBottomColor: colors.primary.main,
                                    },
                                }}
                            />
                            <IconButton
                                size="small"
                                onClick={handleSaveName}
                                sx={{
                                    color: colors.primary.main,
                                    padding: '4px',
                                }}
                            >
                                <RiCheckLine size={20} />
                            </IconButton>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: colors.neutral.gray800,
                                    fontWeight: 600,
                                }}
                            >
                                {projectName}
                            </Typography>
                            <IconButton
                                size="large"
                                onClick={handleEditName}
                                sx={{
                                    color: colors.neutral.gray600,
                                    padding: '4px',
                                    '&:hover': {
                                        color: colors.neutral.gray800,
                                        backgroundColor: colors.neutral.gray100,
                                    },
                                }}
                            >
                                <RiEditLine size={24} />
                            </IconButton>
                        </Box>
                    )}
                </Box>

                {/* Right Side */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Video/Image Toggle */}
                    <ToggleButtonGroup
                        value={designMode}
                        exclusive
                        onChange={handleDesignModeChange}
                        size="small"
                        sx={{
                            backgroundColor: '#F5F5F5',
                            padding: '4px',
                            borderRadius: '8px',
                            '& .MuiToggleButton-root': {
                                textTransform: 'none',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '6px 12px',
                                minWidth: 'auto',
                                color: '#666666',
                                fontWeight: 400,
                                fontSize: '14px',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                                '&.Mui-selected': {
                                    backgroundColor: 'white',
                                    color: '#000000',
                                    fontWeight: 600,
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                    '&:hover': {
                                        backgroundColor: 'white',
                                    },
                                },
                            },
                        }}
                    >
                        <ToggleButton value="image">
                            <RiImageFill size={24} color={naturalScale[5]} style={{ marginRight: 6 }} />
                            Image
                        </ToggleButton>
                        <ToggleButton value="video">
                            <RiVideoLine size={24} color={naturalScale[5]} style={{ marginRight: 6 }} />
                            Video
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {/* Save Button */}
                    <Button
                        variant="outlined"
                        startIcon={saving ? <CircularProgress size={16} /> : <RiSaveFill size={24} color={naturalScale[5]} />}
                        onClick={handleSave}
                        disabled={saving || !storeId}
                        sx={{
                            textTransform: 'none',
                            color: naturalScale[5],
                            borderColor: naturalScale[5],
                            borderRadius: .5,
                            '&:hover': {
                                borderColor: naturalScale[5],
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                            '&:disabled': {
                                opacity: 0.6,
                            },
                        }}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>

                    {/* Export Button */}
                    <Button
                        variant="outlined"
                        startIcon={<RiExportFill size={24} color={colors.neutral.white} />}
                        onClick={() => setExportDialogOpen(true)}
                        sx={{
                            textTransform: 'none',
                            borderColor: colors.primary.main,
                            color: colors.neutral.white,
                            backgroundColor: colors.primary.main,
                            borderRadius: .5,
                            '&:hover': {
                                borderColor: colors.primary.dark,
                                backgroundColor: colors.primary.main,
                            },
                        }}
                    >
                        Export
                    </Button>

                    {/* Exit Button */}
                    <IconButton
                        onClick={handleExit}
                        sx={{
                            color: colors.neutral.gray700,
                            '&:hover': { backgroundColor: colors.neutral.gray100 },
                        }}
                    >
                        <RiCloseLargeLine size={24} />
                    </IconButton>
                </Box>
            </Box>

            {/* Export Dialog */}
            <ExportDialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
            />
        </>
    );
};

export default TopHeaderBar;

